/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { format } from 'date-fns';
import { CalendarDays, SearchX, Trophy } from 'lucide-react';
import React from 'react';
import { getDateLocale, i18n } from 'shared/i18n';
import UDText from 'ud-ui/components/text';
import IconBadge, { AnalyticsTone, TONE_COLORS } from './icon-badge';

interface RecordCardShellProps {
  icon: React.ReactNode;
  title: string;
  tone?: AnalyticsTone;
  /** Значок справа в шапке: главная цифра рекорда. */
  highlight?: React.ReactNode;
  /** Нет данных за период — вместо тела рисуется пустое состояние. */
  isEmpty?: boolean;
  /** Подвал: соревнование и дата матча. */
  championshipName?: string;
  matchDate?: string;
  className?: string;
  children?: React.ReactNode;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceCard};
  border: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  border-radius: ${({ theme }) => theme.radii.panel};
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevation.low};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
`;

const Body = styled.div`
  flex: 1;
  padding: 4px 20px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  background: ${({ theme }) => theme.colors.SurfacePage};
`;

const Meta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: ${({ theme }) => theme.colors.InkSecondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.InkMuted};
  }
`;

const HighlightPill = styled.div<{ $tone: string }>`
  margin-left: auto;
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.radii.pill};
  color: ${({ theme }) => theme.colors.InkPrimary};
  background: color-mix(in srgb, ${({ $tone }) => $tone} 16%, transparent);
`;

const EmptyBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0 8px;
  color: ${({ theme }) => theme.colors.InkMuted};
`;

/**
 * Общая рама карточек рекордов: шапка со значком и главной цифрой, тело, подвал с турниром и датой.
 * Отдельные рекорды различаются только телом.
 */
const RecordCardShell = ({
  icon,
  title,
  tone = 'gold',
  highlight,
  isEmpty,
  championshipName,
  matchDate,
  className,
  children,
}: RecordCardShellProps) => {
  return (
    <Card className={className}>
      <Header>
        <IconBadge icon={icon} tone={tone} size={40} />
        <UDText variant="title-4-bold" as="div" className="!text-inkPrimary">
          {title}
        </UDText>
        {!isEmpty && highlight && (
          <HighlightPill $tone={TONE_COLORS[tone]}>
            <UDText variant="title-5" as="div">
              {highlight}
            </UDText>
          </HighlightPill>
        )}
      </Header>

      <Body>
        {isEmpty ? (
          <EmptyBlock>
            <SearchX size={32} />
            <UDText variant="body-2" as="div">
              <Trans>Нет данных за выбранный период</Trans>
            </UDText>
          </EmptyBlock>
        ) : (
          children
        )}
      </Body>

      {!isEmpty && (championshipName || matchDate) && (
        <Footer>
          {championshipName && (
            <Meta>
              <Trophy size={14} />
              <UDText variant="text-2" as="span">
                {championshipName}
              </UDText>
            </Meta>
          )}
          {matchDate && (
            <Meta>
              <CalendarDays size={14} />
              <UDText variant="text-2" as="span">
                {format(new Date(matchDate), 'd MMM yyyy', { locale: getDateLocale(i18n.locale) })}
              </UDText>
            </Meta>
          )}
        </Footer>
      )}
    </Card>
  );
};

export default RecordCardShell;
